import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { ChangeEvent, memo, useEffect, useRef, FocusEvent } from 'react';
import { useLocale } from '../../../../context/Localization/localization';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { KioskFormPasswordFieldProps } from '../FormPasswordField.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormPasswordField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        keyboardLayout,
        shiftAfterSpace,
        shiftAfterPeriod,
        shiftAfterAmpersand,
        hideKeyboardOnClickOut,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        onChangeCallback,
        tabFocusRefContext,
    }: KioskFormPasswordFieldProps) => {
        /**
         * Not all formik validation accounts for TextFields that are null.
         * So even though we pass an undefined validation type to the function,
         * it is still required in order to have null required fields validate.
         */
        const [field, meta, helper] = useField<string>({
            name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: undefined,
            }),
        });
        const { submitCount } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         */
        const valueRef = useRef<string>(field.value ?? '');
        /**
         * The caret position ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal caret position state.
         */
        const caretPositionRef = useRef<number | null>(null);
        /**
         * The focus ref to prevent a stale state when the on-screen keyboard
         * calls any functions that need to know if the field is focused or not.
         */
        const isFocused = useRef<boolean>(false);
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            /**
             * Updates the internal caret position when the internal MUI value finishes changing from the
             * pattern mask.
             */
            if (inputRef.current) {
                inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
            }
        }, [inputRef.current?.value]);

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const getValue = (): GetValueReturn => {
            if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
                const selectionStart: number = inputRef.current.selectionStart;
                const selectionEnd: number = inputRef.current.selectionEnd;

                return {
                    inputValue: valueRef.current,
                    caretPositionStart: selectionStart,
                    caretPositionEnd: selectionEnd,
                };
            }
            else {
                return {
                    inputValue: valueRef.current,
                    caretPositionStart: valueRef.current.length,
                    caretPositionEnd: valueRef.current.length,
                };
            }
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const setValue = ({ value, caretPosition }: SetValueProps): void => {
            onChangeCallback && onChangeCallback();
            caretPositionRef.current = caretPosition;
            valueRef.current = value;
            helper.setValue(value);
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
            return {
                value: value,
                caretPosition: caretPosition,
            };
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onKeyboardInput = (): void => {
            if (inputRef.current && !isFocused.current) {
                inputRef.current.focus();
            }
        };

        const { onFocus, onBlur } = useKeyboardIntegration({
            fieldValue: field.value,
            getValue: getValue,
            setValue: setValue,
            onInputChange: onInputChange,
            onKeyboardInput: onKeyboardInput,
            keyboardLayout: keyboardLayout,
            shiftAfterPeriod: shiftAfterPeriod,
            shiftAfterSpace: shiftAfterSpace,
            shiftAfterAmpersand: shiftAfterAmpersand,
            hideKeyboardOnClickOut: hideKeyboardOnClickOut,
            capsLock: capsLock,
        });

        const handleOnBlur = () => {
            onBlur();
            isFocused.current = false;
        };

        /**
         * This function is called by the pattern mask onAccept function for on-screen keyboard input
         * or called when phsyical keyboard input is entered.
         */
        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChangeCallback && onChangeCallback();
            caretPositionRef.current = event.target.selectionEnd;
            valueRef.current = event.target.value;
            helper.setValue(event.target.value);
        };

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            isFocused.current = true;
            onFocus(event);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'password'}
                inputRef={inputRef}
                value={valueRef.current}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            />
        );
    },
);
